import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  BookmarkSimple,
  CalendarBlank,
  CaretRight,
  ClosedCaptioning,
  Copy,
  CornersOut,
  DotsThree,
  DownloadSimple,
  FlowArrow,
  Play,
  SlidersHorizontal,
  SpeakerHigh,
  X,
} from '@phosphor-icons/react';
import {
  Badge,
  Button,
  ConfirmDialog,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Stack,
  Tag,
  TextInput,
  toast,
} from '@reelplicate/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@reelplicate/ui/components/ui/dialog';
import { Fragment, type ReactNode, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { PlatformStack } from '@/features/calendar/platform-stack';
import { canMakeMyVersion, useMakeMyVersion } from '@/features/flows/use-make-my-version';

import { CarouselModal, CarouselPreview } from './carousel-viewer';
import { useDownload } from './download';
import { GenerationSection } from './generation-section';
import {
  collectionItems,
  collectionsHolding,
  formatDuration,
  formatViews,
  sceneSaves,
  slideCaptions,
  STATUS,
  typeLabel,
} from './library';
import { makeAgainRequest } from './make-again';
import { useMedia } from './media-context';
import { useMediaOutlet } from './media-outlet';
import { type ReplicateRequest, ReplicateSceneModal } from './replicate-scene-modal';
import { SceneList, ScenePlayer, type VideoShape } from './scene-list';
import { useReplicateScene, useSaveClip, useSceneReplicas } from './scenes';
import type { Asset, Collection, Scene, SlideText } from './types';

const soon = (what: string) => () => toast(`${what} is coming soon`);

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center">
        <h3 className="text-label-md text-text-primary flex-1">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

const asideLink =
  'text-caption text-text-brand rounded-xs outline-none hover:underline focus-visible:shadow-focus-ring';

/** Underlines the search words in the transcript (Figma: "latte art"). */
function Highlighted({ text, query }: { text: string; query: string }) {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 1)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (words.length === 0) return <>{text}</>;
  const parts = text.split(new RegExp(`(${words.join('|')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="text-text-primary underline">
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

type AssetDrawerProps = {
  /** The open asset, or null when closed. */
  asset: Asset | null;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  /** The current search, underlined in the transcript. */
  query: string;
};

/**
 * Figma "Screen — Media · Asset detail" (189:1292): a 460px side sheet with
 * the preview, where it came from, tags, transcript, collections and where
 * it's used. ← / → step through the current results.
 */
export function AssetDrawer({
  asset,
  onClose,
  onPrevious,
  onNext,
  query,
}: AssetDrawerProps) {
  return (
    <Dialog open={asset !== null} onOpenChange={(open) => !open && onClose()}>
      {asset && (
        <DialogContent
          aria-describedby={undefined}
          className="bg-bg-surface border-border-subtle data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-6 fixed inset-y-0 right-0 z-50 flex h-dvh w-[460px] max-w-full flex-col border-l shadow-[-8px_0_32px_0_rgba(0,0,0,0.1)] duration-240 ease-out"
          onKeyDown={(e) => {
            if (e.target instanceof HTMLInputElement) return;
            if (e.key === 'ArrowLeft' && onPrevious) onPrevious();
            if (e.key === 'ArrowRight' && onNext) onNext();
          }}
        >
          <AssetDetails
            key={asset.id}
            asset={asset}
            onPrevious={onPrevious}
            onNext={onNext}
            query={query}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

function AssetDetails({
  asset,
  onPrevious,
  onNext,
  query,
}: {
  asset: Asset;
  onPrevious?: () => void;
  onNext?: () => void;
  query: string;
}) {
  const {
    workspaceId,
    assets,
    collections,
    addTag,
    removeTag,
    addToCollection,
    removeFromCollection,
    trash,
  } = useMedia();
  const { newCaption } = useMediaOutlet();
  const makeMyVersion = useMakeMyVersion();
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [slide, setSlide] = useState(0);
  const [viewingSlides, setViewingSlides] = useState(false);
  const [playing, setPlaying] = useState<{
    scene: number;
    start: number;
    end: number;
    key: number;
  }>();
  const clips = useSaveClip(workspaceId);
  const replicating = useReplicateScene(workspaceId);
  const downloads = useDownload(workspaceId);
  // What the Replicate window remakes: a scene of this reel, or (Make again)
  // the scene a video was made from, filled in with how it was made.
  const [replicateTarget, setReplicateTarget] = useState<{
    reel: Asset;
    scene: Scene;
    initial?: Partial<ReplicateRequest>;
  } | null>(null);
  const [shape, setShape] = useState<VideoShape>('portrait');
  // A saved reel plays its kept video; a made or uploaded one, its file.
  const playable =
    (asset.kind === 'reel' || asset.kind === 'clip') &&
    Boolean(asset.videoUrl ?? asset.fileUrl);
  const scenes = asset.scenes?.length ? asset.scenes : undefined;
  const replicas = useSceneReplicas(workspaceId, asset.id, Boolean(scenes));
  const savedScenes = useMemo(() => {
    const saved = new Map<number, { captionsIn?: Collection[]; clips: Asset[] }>();
    for (const [n, { captions, clips: sceneClips }] of sceneSaves(asset, assets)) {
      saved.set(n, {
        captionsIn: captions.length
          ? collectionsHolding(collections, captions)
          : undefined,
        clips: sceneClips,
      });
    }
    return saved;
  }, [asset, assets, collections]);
  const slideUrls = asset.kind === 'carousel' ? asset.slideUrls : undefined;
  const details = asset.details;
  const inCollections = collections.filter((c) =>
    collectionItems(c, assets).some((a) => a.id === asset.id),
  );
  const others = collections.filter((c) => !c.smart && !inCollections.includes(c));
  const savedSlides = useMemo(() => {
    const saved = new Map<number, Collection[]>();
    for (const [n, captions] of slideCaptions(asset, assets)) {
      saved.set(n, collectionsHolding(collections, captions));
    }
    return saved;
  }, [asset, assets, collections]);
  const badge = [typeLabel(asset), asset.duration && formatDuration(asset.duration)]
    .filter(Boolean)
    .join(' · ');

  const stats = [
    asset.views !== undefined && ['Views', formatViews(asset.views)],
    details?.avgWatch && ['Avg watch', details.avgWatch],
    details?.engagement && ['Engagement', details.engagement],
  ].filter(Boolean) as [string, string][];

  const media = (
    <>
      {slideUrls && slideUrls.length > 1 ? (
        <>
          <CarouselPreview
            slides={slideUrls}
            title={asset.title}
            index={slide}
            onIndexChange={setSlide}
            onExpand={() => setViewingSlides(true)}
          />
          <CarouselModal
            open={viewingSlides}
            onOpenChange={setViewingSlides}
            slides={slideUrls}
            title={asset.title}
            index={slide}
            onIndexChange={setSlide}
            text={asset.slideText}
          />
        </>
      ) : playable ? (
        <ScenePlayer asset={asset} scene={playing} shape={shape} onShape={setShape} />
      ) : (
        <Preview asset={asset} />
      )}
    </>
  );
  const info = (
    <div className="flex min-w-0 flex-1 flex-col gap-3">
      <DialogTitle className="text-heading-h3 text-text-primary">
        {asset.title}
      </DialogTitle>
      <div className="flex flex-wrap gap-1.5">
        <Badge size="sm" tone="brand">
          {asset.source === 'saved'
            ? `Saved from ${asset.savedFrom}`
            : asset.source === 'generated'
              ? 'Generated'
              : 'Uploaded'}
        </Badge>
        {asset.status && (
          <Badge size="sm" dot tone={STATUS[asset.status].tone}>
            {STATUS[asset.status].label}
          </Badge>
        )}
      </div>
      {asset.platforms && <PlatformStack platforms={asset.platforms} />}
      {asset.sourceUrl && (
        <a
          href={asset.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${asideLink} inline-flex w-fit items-center gap-1`}
        >
          View original
          <ArrowSquareOut aria-hidden className="size-3" />
        </a>
      )}
      {stats.length > 0 && (
        <dl className="bg-bg-subtle flex flex-col gap-2 rounded-[10px] p-3">
          {stats.map(([label, value]) => (
            <div key={label} className="flex justify-between">
              <dt className="text-caption text-text-secondary">{label}</dt>
              <dd className="text-body-sm-strong text-text-primary">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="text-caption text-text-tertiary">
        {details?.specs ??
          `Added ${asset.addedDaysAgo === 0 ? 'today' : `${asset.addedDaysAgo}d ago`}`}
      </p>
    </div>
  );
  const tagsSection = (
    <Section
      title="Tags"
      aside={
        asset.tags.length > 0 && (
          <span className="text-caption text-text-brand">Auto-tagged</span>
        )
      }
    >
      <ul className="flex flex-wrap gap-1.5">
        {asset.tags.map((tag) => (
          <li key={tag}>
            <Tag onRemove={() => removeTag(asset.id, tag)}>{tag}</Tag>
          </li>
        ))}
        <li>
          {adding ? (
            <TextInput
              autoFocus
              aria-label="New tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onBlur={() => setAdding(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setAdding(false);
                }
                if (e.key === 'Enter' && newTag.trim()) {
                  addTag(asset.id, newTag.trim());
                  setNewTag('');
                }
              }}
              className="h-6 w-32"
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="rounded-sm outline-none focus-visible:shadow-focus-ring"
            >
              <Tag>+ Add tag</Tag>
            </button>
          )}
        </li>
      </ul>
    </Section>
  );
  const generationSection = asset.generation && (
    <GenerationSection
      generation={asset.generation}
      assets={assets}
      onMakeAgain={(reel, scene) =>
        setReplicateTarget({ reel, scene, initial: makeAgainRequest(asset.generation!) })
      }
    />
  );
  // Landscape videos stack: a side-by-side row would squeeze the title.
  const wide = Boolean(playable) && shape === 'landscape';

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pt-5 pb-6">
        <div className="flex items-center gap-1">
          <Badge size="sm">{badge}</Badge>
          <span className="flex-1" />
          <IconButton
            icon={ArrowLeft}
            size="sm"
            aria-label="Previous"
            disabled={!onPrevious}
            onClick={onPrevious}
          />
          <IconButton
            icon={ArrowRight}
            size="sm"
            aria-label="Next"
            disabled={!onNext}
            onClick={onNext}
          />
          <Menu>
            <MenuTrigger asChild>
              <IconButton icon={DotsThree} size="sm" aria-label="More actions" />
            </MenuTrigger>
            <MenuContent align="end">
              <MenuItem onSelect={soon('Renaming')}>Rename</MenuItem>
              <MenuItem onSelect={soon('Duplicating')}>Duplicate</MenuItem>
              <MenuItem type="danger" onSelect={() => setConfirmingDelete(true)}>
                Delete
              </MenuItem>
            </MenuContent>
          </Menu>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title={`Delete “${asset.title}”?`}
            description="Its file is deleted too, and it leaves every collection. This can’t be undone."
            confirmLabel="Delete"
            onConfirm={() => {
              trash([asset.id]);
              toast(`Deleted “${asset.title}”`);
            }}
          />
          <DialogClose asChild>
            <IconButton icon={X} size="sm" aria-label="Close" />
          </DialogClose>
        </div>

        {wide ? (
          // A landscape video: the title across the top, the video full width
          // under it, then its tags.
          <Stack gap={4}>
            {info}
            {media}
            {tagsSection}
            {generationSection}
          </Stack>
        ) : (
          <div className="flex items-start gap-4">
            {media}
            {info}
          </div>
        )}

        {details?.madeFrom && (
          <Section
            title="Made from"
            aside={
              <button type="button" className={asideLink} onClick={soon('Flows')}>
                Open in flow
              </button>
            }
          >
            <ol className="flex flex-wrap items-center gap-1.5">
              {details.madeFrom.map((step, i) => (
                <li key={step.label} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <CaretRight aria-hidden className="text-icon-tertiary size-3" />
                  )}
                  <span className="border-border-subtle text-caption text-text-primary flex items-center gap-1.5 rounded-md border px-2 py-1">
                    {step.thumbnail && (
                      <img
                        src={step.thumbnail}
                        alt=""
                        className="-my-0.5 -ml-1 h-7 w-4 rounded-[3px] object-cover"
                      />
                    )}
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {!wide && tagsSection}
        {!wide && generationSection}

        {(asset.transcript ?? asset.caption ?? scenes) && (
          <Section
            title={asset.transcript || scenes ? 'Transcript' : 'Caption'}
            aside={
              asset.transcript &&
              !slideUrls?.length &&
              !scenes && (
                <button
                  type="button"
                  className={asideLink}
                  onClick={soon('The full transcript')}
                >
                  Full
                </button>
              )
            }
          >
            {scenes ? (
              // A reel's transcript, scene by scene, like a carousel's slides.
              <SceneList
                scenes={scenes}
                current={playing?.scene}
                onPlay={
                  playable
                    ? ({ scene, start, end }) =>
                        setPlaying({ scene, start, end, key: Date.now() })
                    : undefined
                }
                saved={savedScenes}
                onSaveCaption={({ scene, text }) =>
                  newCaption({
                    caption: text,
                    // The DTO caps titles at 200 characters.
                    title: [...`${asset.title} · Scene ${scene}`].slice(0, 200).join(''),
                    source: { assetId: asset.id, scene },
                  })
                }
                onSaveClip={
                  // Clips are cut from a saved reel's kept video.
                  asset.videoUrl
                    ? ({ scene }) => void clips.save(asset, scene)
                    : undefined
                }
                savingScene={clips.savingScene}
                onReplicate={(scene) => setReplicateTarget({ reel: asset, scene })}
                replicas={replicas}
              />
            ) : asset.slideText?.length ? (
              <SlideTranscript
                slides={asset.slideText}
                query={query}
                // A slide's text shows its slide in the preview, when there is one.
                onShow={
                  slideUrls && slideUrls.length > 1
                    ? (n) => setSlide(Math.min(n - 1, slideUrls.length - 1))
                    : undefined
                }
                current={slideUrls && slideUrls.length > 1 ? slide + 1 : undefined}
                saved={savedSlides}
                onSave={({ slide: n, text }) =>
                  newCaption({
                    caption: text,
                    // The DTO caps titles at 200 characters.
                    title: [...`${asset.title} · Slide ${n}`].slice(0, 200).join(''),
                    source: { assetId: asset.id, slide: n },
                  })
                }
              />
            ) : (
              <blockquote className="bg-bg-subtle text-body-sm text-text-secondary rounded-[10px] px-3 py-2.5 whitespace-pre-line">
                “
                <Highlighted
                  text={asset.transcript ?? asset.caption ?? ''}
                  query={query}
                />
                ”
              </blockquote>
            )}
          </Section>
        )}

        <Section title="In collections">
          <ul className="flex flex-wrap gap-1.5">
            {inCollections.map((c) => (
              <li key={c.id}>
                {c.smart ? (
                  <Tag leadingIcon={SlidersHorizontal}>{c.name}</Tag>
                ) : (
                  <Tag onRemove={() => removeFromCollection(c.id, asset.id)}>
                    {c.name}
                  </Tag>
                )}
              </li>
            ))}
            {others.length > 0 && (
              <li>
                <Menu>
                  <MenuTrigger className="rounded-sm outline-none focus-visible:shadow-focus-ring">
                    <Tag>+ Add to collection</Tag>
                  </MenuTrigger>
                  <MenuContent>
                    {others.map((c) => (
                      <MenuItem
                        key={c.id}
                        onSelect={() => {
                          addToCollection(c.id, [asset.id]);
                          toast.success(`Added to “${c.name}”`);
                        }}
                      >
                        {c.name}
                      </MenuItem>
                    ))}
                  </MenuContent>
                </Menu>
              </li>
            )}
          </ul>
        </Section>

        <Section title="Used in">
          {details?.usedIn?.length ? (
            <ul className="flex flex-col gap-2">
              {details.usedIn.map((use) => {
                const Icon = use.kind === 'flow' ? FlowArrow : CalendarBlank;
                return (
                  <li key={use.title}>
                    <button
                      type="button"
                      onClick={soon(use.kind === 'flow' ? 'Flows' : 'Opening posts')}
                      className="border-border-subtle hover:bg-bg-subtle focus-visible:shadow-focus-ring flex w-full items-center gap-2.5 rounded-md border px-2.5 py-2 text-left outline-none"
                    >
                      <Icon aria-hidden className="text-icon-secondary size-4" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-body-sm-strong text-text-primary truncate">
                          {use.title}
                        </span>
                        <span className="text-caption text-text-tertiary">
                          {use.meta}
                        </span>
                      </span>
                      <CaretRight aria-hidden className="text-icon-tertiary size-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-body-sm text-text-secondary">
              Not used in a flow or post yet.
            </p>
          )}
        </Section>
      </div>

      <div className="border-border-subtle flex gap-2 border-t px-6 py-4">
        {canMakeMyVersion(asset.kind) ? (
          <Button
            leadingIcon={FlowArrow}
            className="flex-1"
            disabled={!makeMyVersion.ready}
            loading={makeMyVersion.pendingAssetId === asset.id}
            onClick={() => void makeMyVersion.start(asset)}
          >
            Make my version
          </Button>
        ) : (
          <Button leadingIcon={FlowArrow} className="flex-1" onClick={soon('Flows')}>
            Use in flow
          </Button>
        )}
        <Button
          variant="outline"
          leadingIcon={CalendarBlank}
          className="flex-1"
          onClick={soon('Scheduling')}
        >
          Schedule
        </Button>
        <IconButton
          icon={DownloadSimple}
          variant="outline"
          aria-label="Download"
          disabled={downloads.pending}
          onClick={() => void downloads.download([asset])}
        />
      </div>
      <ReplicateSceneModal
        scene={replicateTarget?.scene ?? null}
        initial={replicateTarget?.initial}
        pending={replicating.pending}
        onOpenChange={(open) => !open && setReplicateTarget(null)}
        onReplicate={(scene, request) =>
          replicateTarget &&
          void replicating
            .replicate(replicateTarget.reel, scene.scene, request)
            .then((ok) => ok && setReplicateTarget(null))
        }
      />
    </>
  );
}

const BookmarkFilled = (props: { className?: string }) => (
  <BookmarkSimple weight="fill" {...props} />
);

/**
 * A carousel's transcript, slide by slide: what the creator wrote, then what's
 * behind it in muted italics. Choosing a slide shows it in the preview; a
 * slide's text can be copied or saved as a caption, and a saved slide says
 * where to.
 */
function SlideTranscript({
  slides,
  query,
  onShow,
  current,
  saved,
  onSave,
}: {
  slides: SlideText[];
  query: string;
  onShow?: (slide: number) => void;
  current?: number;
  /** Slides saved as captions, with the collections those captions are in. */
  saved?: Map<number, Collection[]>;
  onSave?: (slide: SlideText) => void;
}) {
  const idPrefix = useId();
  const copy = async (slide: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Caption copied');
    } catch {
      // No clipboard (plain http, old browsers): select it to copy by hand.
      const node = document.getElementById(`${idPrefix}-text-${slide}`);
      if (node) window.getSelection()?.selectAllChildren(node);
      toast.info('Caption selected - copy it from the menu');
    }
  };
  return (
    <ol className="bg-bg-subtle flex flex-col gap-1 rounded-[10px] p-1.5">
      {slides.map(({ slide, text, context }) => {
        const body = (
          <>
            <span className="text-caption text-text-tertiary">Slide {slide}</span>
            {text && (
              <span
                id={`${idPrefix}-text-${slide}`}
                className="text-body-sm text-text-secondary whitespace-pre-line"
              >
                <Highlighted text={text} query={query} />
              </span>
            )}
            {context && (
              <span className="text-caption text-text-tertiary italic">
                <Highlighted text={context} query={query} />
              </span>
            )}
          </>
        );
        const saveable = onSave && text;
        const savedTo = saved?.get(slide);
        // Room for the copy and save buttons, which sit over the row's top right.
        const row = `flex w-full flex-col gap-0.5 rounded-md py-1.5 pl-2 text-left ${saveable ? 'pr-[4.5rem]' : text ? 'pr-9' : 'pr-2'}`;
        return (
          <li key={slide} className="group relative">
            {onShow ? (
              <button
                type="button"
                aria-current={slide === current || undefined}
                onClick={() => onShow(slide)}
                className={`${row} hover:bg-bg-muted focus-visible:shadow-focus-ring aria-current:bg-bg-muted outline-none`}
              >
                {body}
              </button>
            ) : (
              <div className={row}>{body}</div>
            )}
            {savedTo && (
              // Outside the row too: the collections are links.
              <div className="flex flex-wrap items-center gap-1 px-2 pb-1.5">
                <span className="text-caption text-text-tertiary">Saved to</span>
                {savedTo.length > 0 ? (
                  savedTo.map((c) => (
                    <Link
                      key={c.id}
                      to={`/media/collections/${c.id}`}
                      className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                    >
                      <Tag>{c.name}</Tag>
                    </Link>
                  ))
                ) : (
                  <Link
                    to="/media?type=caption"
                    className="focus-visible:shadow-focus-ring rounded-sm outline-none"
                  >
                    <Tag>Captions</Tag>
                  </Link>
                )}
              </div>
            )}
            {text && (
              <IconButton
                icon={Copy}
                size="sm"
                aria-label={`Copy slide ${slide} caption`}
                onClick={() => void copy(slide, text)}
                className={`absolute top-1 ${saveable ? 'right-9' : 'right-1'} opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100`}
              />
            )}
            {saveable && (
              // A sibling of the row: the row is itself a button.
              <IconButton
                icon={savedTo ? BookmarkFilled : BookmarkSimple}
                size="sm"
                aria-label={
                  savedTo
                    ? `Slide ${slide} is saved as a caption - save it again`
                    : `Save slide ${slide} as caption`
                }
                onClick={() => onSave({ slide, text, context })}
                className={`absolute top-1 right-1 ${savedTo ? 'text-icon-brand' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100'}`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** Figma "Video Player" (110:14977), paused: poster, big play, controls on a scrim. */
function Preview({ asset }: { asset: Asset }) {
  if (asset.kind === 'caption') {
    return (
      <div className="bg-bg-subtle border-border-subtle text-body-sm text-text-primary h-80 w-45 shrink-0 overflow-hidden rounded-md border p-3">
        {asset.caption}
      </div>
    );
  }
  const timed = asset.duration !== undefined;
  return (
    <div
      className={
        asset.kind === 'audio'
          ? 'bg-bg-inverse relative h-80 w-45 shrink-0 overflow-hidden rounded-md'
          : 'bg-bg-muted relative h-80 w-45 shrink-0 overflow-hidden rounded-md'
      }
    >
      {asset.thumbnail && (
        <img
          src={asset.thumbnail}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />
      )}
      {timed && (
        <>
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-15 bg-gradient-to-b from-[rgba(38,39,25,0)] to-[rgba(38,39,25,0.75)]"
          />
          <button
            type="button"
            aria-label={`Play ${asset.title}`}
            onClick={soon('Playback')}
            className="bg-brand-primary focus-visible:shadow-focus-ring absolute top-1/2 left-1/2 flex size-8 -translate-1/2 items-center justify-center rounded-full shadow-md outline-none"
          >
            <Play weight="fill" aria-hidden className="text-text-on-brand size-[13px]" />
          </button>
          <div
            aria-hidden
            className="text-text-inverse absolute inset-x-0 bottom-0 flex flex-col gap-0.5 px-1.5 pb-1.5"
          >
            <div className="relative h-2">
              <span className="bg-text-inverse/28 absolute inset-x-0 top-[3px] h-0.5 rounded-[2px]" />
              <span className="bg-brand-primary absolute top-[3px] left-0 h-0.5 w-0 rounded-[2px]" />
            </div>
            <div className="flex items-center gap-px">
              <Play weight="fill" className="m-[3px] size-2.5" />
              <span className="text-[7px] leading-[10px] font-medium">
                0:00 / {formatDuration(asset.duration!)}
              </span>
              <span className="flex-1" />
              <ClosedCaptioning className="m-[3px] size-2.5" />
              <SpeakerHigh className="m-[3px] size-2.5" />
              <CornersOut className="m-[3px] size-2.5" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
